import { Lazy } from "common/data/lazy";
import { deprecated, partial, union } from "common/data/schema";
import type { ValidationResult, ValidationSuccessResult } from "common/data/schemaValidator";
import { SchemaValidator, formatValidationErrorResult } from "common/data/schemaValidator";
import { assert, assertIsStringOrNumber } from "common/utils/assertOfType";
import { DocSchemaLibrary, type DocSchemaNameToType } from "schemas-common/docSchema";

const validator = new Lazy(() => new SchemaValidator(DocSchemaLibrary));

export function assertValidatesSchema<T extends keyof DocSchemaNameToType>(
  value: unknown,
  schemaName: T,
): asserts value is DocSchemaNameToType[T] {
  const v = validator.value;

  const result = v.validate(value, DocSchemaLibrary[schemaName]);

  if (result.type !== "SUCCESS") {
    assert(false, formatValidationErrorResult(result));
  }
}

export function assertValidatesUnionSchema<T extends keyof DocSchemaNameToType, S extends keyof DocSchemaNameToType>(
  value: unknown,
  schemaS: S,
  schemaT: T,
): asserts value is DocSchemaNameToType[S] | DocSchemaNameToType[T] {
  const v = validator.value;

  const unionSchema = union([DocSchemaLibrary[schemaS], DocSchemaLibrary[schemaT]]);
  const result = v.validate(value, unionSchema);

  if (result.type !== "SUCCESS") {
    assert(false, formatValidationErrorResult(result));
  }
}

export function assertValidatesPartialSchema<T extends keyof DocSchemaNameToType>(
  value: unknown,
  schema: T,
): asserts value is Partial<DocSchemaNameToType[T]> {
  const v = validator.value;

  const result = v.validate(value, partial(DocSchemaLibrary[schema]));

  if (result.type !== "SUCCESS") {
    assert(false, formatValidationErrorResult(result));
  }
}

// This could be extended to support more than two arguments.
export function assertValidatesPartialUnionSchema<
  S extends keyof DocSchemaNameToType,
  T extends keyof DocSchemaNameToType,
>(value: unknown, schemaS: S, schemaT: T): asserts value is Partial<DocSchemaNameToType[S] | DocSchemaNameToType[T]> {
  const v = validator.value;

  const partialUnionSchema = partial(union([DocSchemaLibrary[schemaS], DocSchemaLibrary[schemaT]]));
  const result = v.validate(value, partialUnionSchema);

  if (result.type !== "SUCCESS") {
    assert(false, formatValidationErrorResult(result));
  }
}

export function assertValidatesDeprecatedSchema<T extends keyof DocSchemaNameToType>(
  value: unknown,
  schema: T,
): asserts value is DocSchemaNameToType[T] {
  const v = validator.value;

  const deprecatedSchema = deprecated(DocSchemaLibrary[schema], {
    date: new Date().toISOString(),
    comment: "deprecated",
  });

  const result = v.validate(value, deprecatedSchema);

  if (result.type !== "SUCCESS") {
    assert(false, formatValidationErrorResult(result));
  }

  assertValidationSuccess(result);

  assert(result.warnings.length > 0);
}

export function assertValidationSuccess(result: ValidationResult): asserts result is ValidationSuccessResult {
  assert(result.type === "SUCCESS");
}

export function assertValidatesSchemaTODO<T>(_value: unknown): asserts _value is T {
  // TODO
}

// TODO: replace this with KeyOf schema validation.
export function assertIsKeyOfInterfaceSchema<T extends keyof DocSchemaNameToType>(
  value: unknown,
  schema: T,
): asserts value is keyof DocSchemaNameToType[T] {
  const s = DocSchemaLibrary[schema];
  assertIsStringOrNumber(value);
  assert(s.kind === "INTERFACE" && value in s.fields);
}
